/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Fleet health › PCs (docs/CONTRACT-fleet-governance.md section 12.2): every PC from GET /v1/admin/fleet/devices, every
 * column the service can sort sortable, cursor paging, the filters kept in the address (status, version, ring, problem,
 * the search box with a 300 ms pause, Show gone, the sort), Export NDJSON with the same filters; a row opens the PC drawer.
 */
import { useEffect, useState } from 'react';
import { Monitor, Search } from 'lucide-react';
import {
  FLEET_PROBLEM_KINDS, FLEET_STATUSES, PROBLEM_LABELS, RINGS, STATUS_CHIPS, VERSION_CHIPS, ageText, fleetApi, fleetExportHref, fleetFailure,
  parseSort, policyBehind, policyText, problemChip, pushText, ringLabel, sortParam, sourcesFailedText, statusChip, toggleSort,
  type FleetDeviceRow, type FleetList, type SortState,
} from '../../lib/fleet';
import { Checkbox, EmptyState, Icon, Input, PageHeader, Panel, Select, Stack, StatusChip, Table, type Column } from '../../ui';
import { count } from '../../ui/format';
import { usePagedList, useQueryParam } from '../pulse/common';
import { Chip, ExportLink, LoadError, SourcesNote, type FleetFrame } from './parts';

const DEFAULT_SORT: SortState = { key: 'computerName', direction: 'asc' };
/** Columns that sort as words (ascending first); the rest sort as numbers, versions or times (descending first). */
const TEXT_COLUMNS = new Set(['computerName', 'ownerEmail', 'ring']);
const SEARCH_PAUSE_MS = 300;

function switchChips(row: FleetDeviceRow) {
  const chips = [
    row.mcpOff ? <StatusChip key="mcp" tone="warn" plain>MCP off</StatusChip> : null,
    row.quarantined ? <StatusChip key="q" tone="warn" plain>Quarantined</StatusChip> : null,
    row.disabled ? <StatusChip key="d" tone="bad" plain>Gyro disabled</StatusChip> : null,
  ].filter(Boolean);
  return chips.length ? <span className="ui-chips">{chips}</span> : <span className="ui-quiet">—</span>;
}

export function FleetPcs({ frame }: { frame: FleetFrame }) {
  const [status, setStatus] = useQueryParam('status');
  const [version, setVersion] = useQueryParam('version');
  const [ring, setRing] = useQueryParam('ring');
  const [problem, setProblem] = useQueryParam('problem');
  const [q, setQ] = useQueryParam('q');
  const [showGone, setShowGone] = useQueryParam('showGone');
  const [sortText, setSortText] = useQueryParam('sort');
  const sort = parseSort(sortText, DEFAULT_SORT);

  // The search box and the version box send after a 300 ms pause, so typing does not ask the service on every key.
  const [typed, setTyped] = useState(q ?? '');
  const [typedVersion, setTypedVersion] = useState(version ?? '');
  useEffect(() => {
    const timer = setTimeout(() => {
      if (typed.trim() !== (q ?? '')) setQ(typed.trim() || null);
      if (typedVersion.trim() !== (version ?? '')) setVersion(typedVersion.trim() || null);
    }, SEARCH_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [typed, typedVersion, q, version, setQ, setVersion]);

  const filters = {
    status: status || undefined, version: version || undefined, ring: ring || undefined, problem: problem || undefined, q: q || undefined,
    showGone: showGone === '1',
  };
  const params = { ...filters, sort: sortParam(sort) };
  const list = usePagedList((cursor) => fleetApi.devices({ ...params, cursor, limit: 50 }), JSON.stringify(params));
  const sources = list.load.state === 'ready' ? (list.load.data as FleetList<FleetDeviceRow>).sourcesFailed : null;
  const filtered = Boolean(status || version || ring || problem || q);

  const columns: Array<Column<FleetDeviceRow>> = [
    { key: 'computerName', header: 'PC', sortable: true, cell: (row) => <span title={row.deviceId}>{row.computerName ?? <span className="ui-quiet">Unnamed PC</span>}</span> },
    { key: 'ownerEmail', header: 'Person', sortable: true, hideOnPhone: true, cell: (row) => row.ownerEmail ?? '—' },
    {
      key: 'agentVersion', header: 'Version', sortable: true,
      cell: (row) => <span className="ui-chips"><Chip spec={VERSION_CHIPS[row.versionChip] ?? VERSION_CHIPS.unknown} /><span className="is-mono">{row.agentVersion ?? '—'}</span></span>,
    },
    { key: 'ring', header: 'Ring', sortable: true, hideOnPhone: true, cell: (row) => ringLabel(row.ring) },
    {
      key: 'lastSeenAt', header: 'Last seen', sortable: true,
      cell: (row) => <span className="ui-cell-stack"><Chip spec={statusChip(row.status)} /><span className="ui-cell-sub">{ageText(row.lastSeenAt)}</span></span>,
    },
    { key: 'crashes7d', header: 'Crashes 7 d', numeric: true, sortable: true, cell: (row) => count(row.crashes7d) },
    { key: 'webview2Failures7d', header: 'WebView2 7 d', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => count(row.webview2Failures7d) },
    { key: 'signInFailures7d', header: 'Sign-in 7 d', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => count(row.signInFailures7d) },
    { key: 'switches', header: 'Switches', hideOnPhone: true, cell: switchChips },
    { key: 'policy', header: 'Policy', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => <span className={policyBehind(row.policy) ? 'ui-text-warn' : undefined}>{policyText(row.policy)}</span> },
    { key: 'drops24h', header: 'Push', sortable: true, hideOnPhone: true, cell: (row) => pushText(row.push) },
    {
      key: 'problems', header: 'Problems', sortable: true,
      cell: (row) => (row.problems.length ? <span className="ui-chips">{row.problems.map((kind) => <Chip key={kind} spec={problemChip(kind, row)} />)}</span> : <span className="ui-quiet">none</span>),
    },
  ];

  return (
    <>
      <PageHeader
        tabs={frame.tabs}
        activeTab={frame.activeTab}
        meta={<span>Every PC with Gyro; a row opens the PC.</span>}
        actions={(
          <>
            <ExportLink href={fleetExportHref('devices', filters)} />
            {frame.actions}
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          <SourcesNote text={sourcesFailedText(sources)} />
          <Panel
            flush
            title="PCs"
            description={list.load.state === 'ready' ? `${count(list.total)} PC${list.total === 1 ? '' : 's'}${filtered ? ' match the filters' : ''}${showGone === '1' ? ', gone PCs included' : ''}` : 'Every PC with Gyro'}
            actions={(
              <span className="ui-toolbar">
                <Select size="sm" className="ui-w-auto" aria-label="Status" value={status ?? ''} onChange={(event) => setStatus(event.target.value || null)}>
                  <option value="">Status: All</option>
                  {FLEET_STATUSES.filter((value) => value !== 'gone' || showGone === '1').map((value) => <option key={value} value={value}>{STATUS_CHIPS[value].label}</option>)}
                </Select>
                <Input size="sm" className="ui-w-sm" aria-label="Version" placeholder="Version" value={typedVersion} onChange={(event) => setTypedVersion(event.target.value)} />
                <Select size="sm" className="ui-w-auto" aria-label="Ring" value={ring ?? ''} onChange={(event) => setRing(event.target.value || null)}>
                  <option value="">Ring: All</option>
                  {[...RINGS, 'unknown' as const].map((value) => <option key={value} value={value}>{ringLabel(value)}</option>)}
                </Select>
                <Select size="sm" className="ui-w-auto" aria-label="Problem kind" value={problem ?? ''} onChange={(event) => setProblem(event.target.value || null)}>
                  <option value="">Problem: All</option>
                  {FLEET_PROBLEM_KINDS.map((kind) => <option key={kind} value={kind}>{PROBLEM_LABELS[kind]}</option>)}
                </Select>
                <Input size="sm" prefix={<Icon icon={Search} size="sm" />} aria-label="Search PCs" placeholder="PC, device id or e-mail" value={typed} maxLength={100} onChange={(event) => setTyped(event.target.value)} />
                <Checkbox label="Show gone" checked={showGone === '1'} onChange={(event) => setShowGone(event.target.checked ? '1' : null)} />
              </span>
            )}
          >
            {list.load.state === 'failed' ? <div className="ui-panel-pad"><LoadError title="The PCs could not be loaded" error={list.load.error} words={(error) => fleetFailure(error)} onRetry={list.reload} retrying={list.reloading} /></div> : (
              <Table
                label="PCs"
                columns={columns}
                rows={list.items}
                rowKey={(row) => row.deviceId}
                loading={list.load.state === 'loading'}
                skeletonRows={8}
                onRowClick={(row) => frame.openPc(row.deviceId)}
                sort={{ key: sort.key, direction: sort.direction, onSort: (key) => setSortText(sortParam(toggleSort(sort, key, !TEXT_COLUMNS.has(key)))) }}
                more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
                empty={<EmptyState compact icon={Monitor} title={filtered ? 'No PC matches these filters' : 'No PCs yet'}>{filtered ? 'Try another status, version, ring, problem kind or search.' : 'A PC appears here once it enrols, reports its health or its switches, or sends its logs.'}</EmptyState>}
              />
            )}
            {list.moreError ? <div className="ui-panel-pad ui-panel-pad--top"><LoadError title="The next page could not be loaded" error={list.moreError} words={(error) => fleetFailure(error)} /></div> : null}
          </Panel>
        </Stack>
      </div>
    </>
  );
}
